import { useState, type ReactNode, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditorState } from '@tiptap/react';
import type { Editor } from '@tiptap/core';
import { safeHref } from './sanitize';
import { CANVAS_UI, FLOATING_BAR } from '../editor/canvas-ui';
import { cn, ColorSwatch, IconButton, Input } from '../../ui';

export const TEXT_COLORS = ['#141414', '#6b7280', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#2563eb', '#9333ea'] as const;

/**
 * Floating formatting bar above the edited text, at a constant screen size; `docked` (phones, in a portal): a full-width
 * bar resting on the virtual keyboard (`--os-keyboard-inset`, spec §13). Buttons never take focus from the editor.
 */
export function FormatBar({ editor, ref, docked = false }: { editor: Editor; ref: Ref<HTMLDivElement>; docked?: boolean }) {
  const { t } = useTranslation();
  const [linking, setLinking] = useState(false);
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      h1: e.isActive('heading', { level: 1 }),
      h2: e.isActive('heading', { level: 2 }),
      h3: e.isActive('heading', { level: 3 }),
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      bullet: e.isActive('bulletList'),
      ordered: e.isActive('orderedList'),
      task: e.isActive('taskList'),
      link: e.isActive('link'),
      left: e.isActive({ textAlign: 'left' }),
      center: e.isActive({ textAlign: 'center' }),
      right: e.isActive({ textAlign: 'right' }),
    }),
  });
  const chain = () => editor.chain().focus();
  const b = (label: string, pressed: boolean, run: () => void, body: ReactNode) => (
    <IconButton key={label} label={label} pressed={pressed} size="sm" onMouseDown={(e) => e.preventDefault()} onClick={run} icon={<span className="px-1 text-xs font-semibold">{body}</span>} className="w-auto min-w-7" />
  );
  return (
    <div
      ref={ref}
      role="toolbar"
      aria-label={t('format.label')}
      {...(docked ? { [CANVAS_UI]: '', 'data-docked': '' } : {})}
      className={cn(
        'flex items-center gap-0.5 whitespace-nowrap border-border p-1',
        FLOATING_BAR,
        docked
          ? 'fixed inset-x-0 z-50 overflow-x-auto border-t pl-[max(0.25rem,env(safe-area-inset-left))] pr-[max(0.25rem,env(safe-area-inset-right))] *:pointer-coarse:h-11 *:pointer-coarse:min-w-11'
          : 'absolute bottom-full left-0 mb-2 origin-bottom-left rounded-full',
      )}
      style={docked ? { bottom: 'var(--os-keyboard-inset, 0px)' } : { transform: 'scale(var(--inv-zoom, 1))' }}
    >
      {([1, 2, 3] as const).map((level) => b(t('format.heading', { level }), s[`h${level}`], () => chain().toggleHeading({ level }).run(), `H${level}`))}
      {b(t('format.bold'), s.bold, () => chain().toggleBold().run(), <b>B</b>)}
      {b(t('format.italic'), s.italic, () => chain().toggleItalic().run(), <i>I</i>)}
      {b(t('format.underline'), s.underline, () => chain().toggleUnderline().run(), <u>U</u>)}
      {b(t('format.strike'), s.strike, () => chain().toggleStrike().run(), <s>S</s>)}
      {TEXT_COLORS.map((c) => (
        <ColorSwatch key={c} size="sm" color={c} label={t('format.color', { color: c })} onClick={() => chain().setColor(c).run()} />
      ))}
      {b(t('format.bullet'), s.bullet, () => chain().toggleBulletList().run(), '•')}
      {b(t('format.ordered'), s.ordered, () => chain().toggleOrderedList().run(), '1.')}
      {b(t('format.task'), s.task, () => chain().toggleTaskList().run(), '☑')}
      {b(t('format.alignLeft'), s.left, () => chain().setTextAlign('left').run(), '⇤')}
      {b(t('format.alignCenter'), s.center, () => chain().setTextAlign('center').run(), '↔')}
      {b(t('format.alignRight'), s.right, () => chain().setTextAlign('right').run(), '⇥')}
      {b(t('format.link'), s.link, () => (s.link ? chain().unsetLink().run() : setLinking(true)), '🔗')}
      {linking && (
        <Input
          autoFocus
          aria-label={t('format.linkUrl')}
          placeholder="https://"
          className="ml-1 h-7 w-48 px-1 text-xs"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setLinking(false);
              editor.commands.focus();
            }
            if (e.key !== 'Enter') return;
            const href = safeHref(e.currentTarget.value);
            setLinking(false);
            if (href) chain().setLink({ href }).run();
            else editor.commands.focus();
          }}
        />
      )}
    </div>
  );
}
